import { useEffect, useState } from "react";
import spireLogoUrl from "@/assets/SecuraPath_Logo_filled_no_text.png";
import { Link, useLocation, useMatch } from "react-router-dom";
import { useIsSecuraPathStaff } from "@/hooks/useFrameworkMapping";
import {
  Building2,
  Users,
  LayoutDashboard,
  MapPin,
  Truck,
  Database,
  HardDrive,
  MonitorSmartphone,
  Cog,
  ChevronDown,
  ClipboardList,
  ClipboardCheck,
  FileQuestion,
  CheckSquare,
  Shield,
  AlertTriangle,
  Grid3x3,
  BookOpen,
  FileCheck,
  Link2,
  FileText,
  Cloud,
  Scale,
  Library,
  FolderOpen,
  ListTodo,
  Plus,
  BarChart3,
  ShieldAlert,
  GraduationCap,
  Megaphone,
  ShieldCheck,
  FlaskConical,
  HardDriveDownload,
  ClipboardList as ClipboardListIcon,
  Target,
  PackageCheck,
  CreditCard,
  FileEdit,
  LineChart,
  Network,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

const mainItems = [
  { title: "Clients", url: "/clients", icon: Building2 },
];

const assetItems = [
  { title: "Overview", url: "/assets", icon: LayoutDashboard },
  { title: "Services", url: "/assets/services", icon: Cog },
  // Alphabetical order below
  { title: "Cloud", url: "/assets/cloud", icon: Cloud },
  { title: "Data", url: "/assets/data", icon: Database },
  { title: "Hardware", url: "/assets/hardware", icon: HardDrive },
  { title: "People", url: "/assets/people", icon: Users },
  { title: "Physical", url: "/assets/physical", icon: MapPin },
  { title: "Software", url: "/assets/software", icon: MonitorSmartphone },
  { title: "Suppliers", url: "/assets/suppliers", icon: Truck },
  { title: "Supplier Risk", url: "/supplier-risk-assessment", icon: AlertTriangle },
];

// DDQ items without Question Library (moved to SecuraPath Libraries)
const ddqItems = [
  { title: "Overview", url: "/ddq-responses", icon: ClipboardList },
  // Alphabetical order below
  { title: "Data", url: "/ddq-responses?type=data", icon: Database },
  { title: "Hardware", url: "/ddq-responses?type=hardware", icon: HardDrive },
  { title: "People", url: "/ddq-responses?type=people", icon: Users },
  { title: "Physical", url: "/ddq-responses?type=physical", icon: MapPin },
  { title: "Software", url: "/ddq-responses?type=software", icon: MonitorSmartphone },
  { title: "Suppliers", url: "/ddq-responses?type=supplier", icon: Truck },
  { title: "Review", url: "/ddq-review", icon: CheckSquare },
];

// Document Repository items (client-level)
const documentsItems = [
  { title: "Doc Generation Tracker", url: "/isms-doc-tracker", icon: BarChart3 },
  { title: "ISMS Template Library", url: "/isms-document-library", icon: Library },
];

const riskItems = [
  { title: "Risk Register", url: "/risk-register", icon: AlertTriangle },
  { title: "Risk Matrix", url: "/risk-matrix", icon: Grid3x3 },
  { title: "Risk Treatments", url: "/risk-treatments", icon: ClipboardList },
  { title: "Risk Appetite", url: "/settings/risk-appetite", icon: Scale },
];

const threatIntelItems = [
  { title: "OSINT Findings", url: "/osint-findings", icon: Shield },
  { title: "KEV Catalog", url: "/kev-catalog", icon: AlertTriangle },
];

// Compliance items (client-level) - removed Control-Risk Mapping and ISO Controls (now in SecuraPath Libraries)
const complianceItems = [
  { title: "Statement of Applicability", url: "/soa", icon: FileCheck },
];

// Audit items (M5 Internal Audit Management)
const auditItems = [
  { title: "Audit Programme", url: "/audits", icon: ClipboardCheck },
  { title: "Non-Conformities", url: "/ncrs", icon: AlertTriangle },
];

// Incident Management items
const incidentItems = [
  { title: "Incident Register", url: "/incidents", icon: ShieldAlert },
];

// Training & Awareness items (M6)
const trainingItems = [
  { title: "Training Records", url: "/training", icon: GraduationCap },
  { title: "Campaigns", url: "/training/campaigns", icon: Megaphone },
  { title: "Competency Matrix", url: "/training/competency", icon: Grid3x3 },
];

// Business Continuity items (M7)
const bcpItems = [
  { title: "BCP Overview", url: "/business-continuity", icon: ShieldCheck },
  { title: "Plans", url: "/business-continuity/plans", icon: FileText },
  { title: "Tests", url: "/business-continuity/tests", icon: FlaskConical },
  { title: "Backups", url: "/business-continuity/backups", icon: HardDriveDownload },
];

// CIR (Continual Improvement Register) items
const cirItems = [
  { title: "CIR Overview", url: "/cir", icon: ListTodo },
  { title: "Submit Item", url: "/cir/submit", icon: Plus },
];

// Management Review items (M8)
const managementReviewItems = [
  { title: "Dashboard", url: "/management-review", icon: BarChart3 },
  { title: "Reviews", url: "/management-review/reviews", icon: ClipboardListIcon },
  { title: "KPIs", url: "/management-review/kpis", icon: Target },
  { title: "KPI Trends", url: "/management-review/kpi-trends", icon: LineChart },
];

// SecuraPath Libraries (staff-only) - templates and artefacts at SecuraPath level
const securaPathLibraryItems = [
  { title: "ISO Controls Library", url: "/iso-controls", icon: BookOpen },
  { title: "Legal Requirements", url: "/admin/legal-requirements-library", icon: Scale },
  { title: "DDQ Question Library", url: "/ddq-questions", icon: FileQuestion },
  { title: "Template Library", url: "/admin/template-library", icon: FolderOpen },
  { title: "Security Framework Templates", url: "/admin/framework-template-library", icon: ShieldCheck },
  { title: "Control-Document Map", url: "/control-document-map", icon: Link2 },
  { title: "Control-Risk Mapping", url: "/control-risk-mapping", icon: Link2 },
  { title: "OSINT Findings Library", url: "/admin/osint-library", icon: Shield },
  { title: "Reference Documents", url: "/admin/reference-documents", icon: Library },
  { title: "Framework Evidence Import", url: "/admin/framework-evidence-import", icon: PackageCheck },
  // CIR-2026-543: plan catalogue + assign-plan (Epic 15 FR-01/FR-02)
  { title: "Service Plans", url: "/staff/service-plans", icon: CreditCard },
  // CIR-2026-551: Guided Interface content management (Epic 18 FR-28a, FR-08/FR-09)
  { title: "Guided Interface Content", url: "/staff/guided-content", icon: FileEdit },
  // CIR-2026-550: aggregate de-identified guided-journey drop-off report (Epic 14 FR-42)
  { title: "Guided Drop-off Report", url: "/staff/guided-dropoff-report", icon: BarChart3 },
  // CIR-2026-570: live table relationships + derived-data pipelines
  { title: "Schema Map", url: "/staff/schema-map", icon: Network },
];

/**
 * A sidebar item that is scoped to the CURRENT client. URL is the single source
 * of truth (CIR-2026-354): when the URL is inside a real client context it links
 * to that client's section (NavLink → aria-current=page for free); otherwise it
 * links to the client list with a `pickFor` hint so the list can prompt the user
 * to choose a client first. It NEVER reads `selectedClient` (localStorage) — that
 * was the F61 wrong-client vector.
 */
function ClientContextNavItem({
  segment,
  icon: Icon,
  label,
  tooltip,
  clientIdInUrl,
}: {
  segment: string;
  icon: typeof FileText;
  label: string;
  tooltip: string;
  clientIdInUrl: string | undefined;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={tooltip}>
        {clientIdInUrl ? (
          <NavLink
            to={`/clients/${clientIdInUrl}/${segment}`}
            className="hover:bg-accent/20 hover:text-accent"
            activeClassName="bg-primary/20 text-primary font-medium"
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </NavLink>
        ) : (
          <Link
            to="/clients"
            state={{ pickFor: segment }}
            className="hover:bg-accent/20 hover:text-accent"
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const { data: isStaff } = useIsSecuraPathStaff();

  // URL is the single source of truth for the current client (CIR-2026-354).
  // useMatch gives authoritative param extraction (no hand-rolled regex). The
  // pattern also matches `/clients/new` (a non-client route) with id="new", so
  // we exclude that single literal — it is the only non-:id first segment under
  // `/clients/` in the route table (see App.tsx: `/clients`, `/clients/new`,
  // `/clients/:id/...`). When undefined we are NOT in a client context and
  // client-scoped items fall back to the picker rather than guessing an id.
  const clientRouteMatch = useMatch("/clients/:id/*");
  const matchedId = clientRouteMatch?.params.id;
  const clientIdInUrl = matchedId && matchedId !== "new" ? matchedId : undefined;

  const isAssetRoute = currentPath.startsWith("/assets") || currentPath.startsWith("/supplier-risk");
  const isDdqRoute = currentPath.startsWith("/ddq") && !currentPath.startsWith("/ddq-questions");
  const isDocumentsRoute = currentPath.includes("/isms-library") || currentPath.includes("/document-profile") || currentPath.startsWith("/isms-doc");
  const isRiskRoute = currentPath.startsWith("/risk");
  const isThreatIntelRoute = currentPath.startsWith("/osint") || currentPath.startsWith("/kev");
  const isComplianceRoute =
    currentPath.startsWith("/iso") ||
    currentPath.startsWith("/soa") ||
    currentPath.startsWith("/compliance") ||
    currentPath.includes("/legal-compliance");
  const isAuditRoute = currentPath.startsWith("/audits") || currentPath.startsWith("/ncrs") || currentPath.includes("/audit-pack");
  const isIncidentRoute = currentPath.startsWith("/incidents");
  const isTrainingRoute = currentPath.startsWith("/training");
  const isBcpRoute = currentPath.startsWith("/business-continuity");
  const isCirRoute = currentPath.startsWith("/cir");
  const isManagementReviewRoute = currentPath.startsWith("/management-review");
  const isSecuraPathRoute =
    currentPath.startsWith("/admin") ||
    currentPath.startsWith("/ddq-questions") ||
    currentPath.startsWith("/control-document-map") ||
    currentPath.startsWith("/control-risk-mapping") ||
    currentPath.startsWith("/iso-controls");

  const STORAGE_KEY = "spire.sidebar.openGroups";

  const routeDefaults = {
    assets: isAssetRoute,
    ddq: isDdqRoute,
    documents: isDocumentsRoute,
    risk: isRiskRoute,
    threatIntel: isThreatIntelRoute,
    compliance: isComplianceRoute,
    audit: isAuditRoute,
    incidents: isIncidentRoute,
    training: isTrainingRoute,
    bcp: isBcpRoute,
    cir: isCirRoute,
    managementReview: isManagementReviewRoute,
    securaPath: isSecuraPathRoute,
  } as const;

  const [openGroups, setOpenGroups] = useState<
    Record<keyof typeof routeDefaults, boolean>
  >(() => {
    if (typeof window === "undefined") return { ...routeDefaults };

    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<
          Record<keyof typeof routeDefaults, boolean>
        >;
        return { ...routeDefaults, ...parsed };
      }
    } catch {
      // ignore
    }

    return { ...routeDefaults };
  });

  // Keep the current section expanded (without collapsing other sections)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- state sync from fetched/derived data in an effect; restructuring is a behavioural refactor out of scope for lint clearance (CIR-2026-296)
    setOpenGroups((prev) => {
      let changed = false;
      const next = { ...prev };

      (Object.keys(routeDefaults) as Array<keyof typeof routeDefaults>).forEach(
        (k) => {
          // eslint-disable-next-line security/detect-object-injection -- safe: numeric loop index over a local array (CIR-2026-296)
          if (routeDefaults[k] && !next[k]) {
            // eslint-disable-next-line security/detect-object-injection -- safe: numeric loop index over a local array (CIR-2026-296)
            next[k] = true;
            changed = true;
          }
        }
      );

      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssetRoute, isDdqRoute, isDocumentsRoute, isRiskRoute, isThreatIntelRoute, isComplianceRoute, isAuditRoute, isIncidentRoute, isTrainingRoute, isBcpRoute, isCirRoute, isManagementReviewRoute, isSecuraPathRoute]);

  // Persist expanded/collapsed state across route changes (sidebar can remount)
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(openGroups));
    } catch {
      // ignore
    }
  }, [openGroups]);

  const setGroupOpen = (group: keyof typeof routeDefaults, open: boolean) => {
    setOpenGroups((prev) =>
      // eslint-disable-next-line security/detect-object-injection -- safe: key is an internally-typed constant/enum/config value, not user input (CIR-2026-296)
      prev[group] === open ? prev : { ...prev, [group]: open }
    );
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-3">
          <img 
            src={spireLogoUrl} 
            alt="SecuraPath Logo" 
            className="h-24 w-24 object-contain logo-glow"
          />
          <span
            className={cn(
              // CIR-2026-378: restore the brand multi-colour gradient fill on the
              // wordmark text only (marketing-site treatment). Uses the CIR-2026-361
              // calm-retokenised `--gradient-cyber` (desaturated blue->green, no glow/
              // pulse) so this stays within the low-stimulation theme's intent.
              "text-2xl font-bold text-gradient transition-opacity",
              collapsed && "opacity-0"
            )}
          >
            SPIRE
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <NavLink
                      to={item.url}
                      className="hover:bg-accent/20 hover:text-accent"
                      activeClassName="bg-primary/20 text-primary font-medium"
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <Collapsible
          open={openGroups.assets}
          onOpenChange={(open) => setGroupOpen("assets", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger data-testid="nav-asset-register" className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Asset Register
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {assetItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.url === "/assets"}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.ddq}
          onOpenChange={(open) => setGroupOpen("ddq", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Due Diligence
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {ddqItems.map((item) => {
                    // For DDQ items, check exact match including query params
                    const currentFullPath = `${currentPath}${location.search}`;
                    const isExactMatch = item.url.includes('?') 
                      ? currentFullPath === item.url
                      : item.url === "/ddq-responses" 
                        ? currentPath === "/ddq-responses" && location.search === ""
                        : undefined;
                    
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild tooltip={item.title}>
                          <NavLink
                            to={item.url}
                            end={item.url === "/ddq-responses"}
                            className={cn(
                              "hover:bg-accent/20 hover:text-accent",
                              isExactMatch === true && "bg-primary/20 text-primary font-medium",
                              isExactMatch === false && ""
                            )}
                            activeClassName={isExactMatch === undefined ? "bg-primary/20 text-primary font-medium" : ""}
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.documents}
          onOpenChange={(open) => setGroupOpen("documents", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Document Repository
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {/* ISMS Library - client-specific (URL-as-truth) */}
                  <ClientContextNavItem
                    segment="isms-library"
                    icon={FileText}
                    label="Client ISMS Repository"
                    tooltip="ISMS Document Library"
                    clientIdInUrl={clientIdInUrl}
                  />
                  {documentsItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.risk}
          onOpenChange={(open) => setGroupOpen("risk", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Risk Management
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {riskItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.threatIntel}
          onOpenChange={(open) => setGroupOpen("threatIntel", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Threat Intelligence
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {threatIntelItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.compliance}
          onOpenChange={(open) => setGroupOpen("compliance", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Compliance
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {/* Legal Requirements - client-specific (URL-as-truth) */}
                  <ClientContextNavItem
                    segment="legal-compliance"
                    icon={Scale}
                    label="Legal Requirements"
                    tooltip="Legal Requirements"
                    clientIdInUrl={clientIdInUrl}
                  />
                  {complianceItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                  {/* Controller Actions — staff-only (data-protection follow-ups) */}
                  {isStaff && (
                    <SidebarMenuItem>
                      <SidebarMenuButton asChild tooltip="Controller Actions">
                        <NavLink
                          to="/compliance/controller-actions"
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <ShieldCheck className="h-4 w-4" />
                          <span>Controller Actions</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.audit}
          onOpenChange={(open) => setGroupOpen("audit", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Internal Audit
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {auditItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                  {/* Audit Pack - client-specific (URL-as-truth) */}
                  <ClientContextNavItem
                    segment="audit-pack"
                    icon={PackageCheck}
                    label="Audit Pack"
                    tooltip="Audit Pack"
                    clientIdInUrl={clientIdInUrl}
                  />
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.incidents}
          onOpenChange={(open) => setGroupOpen("incidents", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Incidents
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {incidentItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.training}
          onOpenChange={(open) => setGroupOpen("training", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Training & Awareness
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {trainingItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.url === "/training"}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.bcp}
          onOpenChange={(open) => setGroupOpen("bcp", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Business Continuity
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {bcpItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.url === "/business-continuity"}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.cir}
          onOpenChange={(open) => setGroupOpen("cir", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Continual Improvement
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {cirItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.url === "/cir"}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        <Collapsible
          open={openGroups.managementReview}
          onOpenChange={(open) => setGroupOpen("managementReview", open)}
          className="group/collapsible"
        >
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
              Management Review
              <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {managementReviewItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.url === "/management-review"}
                          className="hover:bg-accent/20 hover:text-accent"
                          activeClassName="bg-primary/20 text-primary font-medium"
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {/* Staff-only SecuraPath Libraries section */}
        {isStaff && (
          <Collapsible
            open={openGroups.securaPath}
            onOpenChange={(open) => setGroupOpen("securaPath", open)}
            className="group/collapsible"
          >
            <SidebarGroup>
              <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-sidebar-foreground/70 hover:text-sidebar-foreground">
                SecuraPath Libraries
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {securaPathLibraryItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton asChild tooltip={item.title}>
                          <NavLink
                            to={item.url}
                            className="hover:bg-accent/20 hover:text-accent"
                            activeClassName="bg-primary/20 text-primary font-medium"
                          >
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
